<template>
  <div
    class="bg-white dark:bg-gray-800 px-2 py-2 sm:p-4 sm:rounded-lg sm:shadow dark:sm:shadow-gray-900/50 sm:mb-6 border-b sm:border-0 border-gray-200 dark:border-gray-700"
  >
    <div class="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4">
      <div
        class="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4 flex-1"
        :class="
          hideAvailabilityMode && !showActivityCategories ? 'md:grid-cols-6' : 'md:grid-cols-7'
        "
      >
        <!-- Search by name -->
        <div class="col-span-2 md:col-span-1">
          <label
            class="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2"
            >Zoeken</label
          >
          <input
            :value="modelValue.search"
            @input="update('search', $event.target.value)"
            type="text"
            placeholder="Zoek op naam..."
            class="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-lg px-2 sm:px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <!-- Team multi-select Listbox -->
        <div class="relative">
          <label
            class="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2"
            >Team</label
          >
          <Listbox
            v-slot="{ open: teamOpen }"
            :model-value="selectedTeamIds"
            @update:model-value="updateTeams"
            multiple
          >
            <div class="relative">
              <ListboxButton
                class="relative w-full cursor-pointer border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 pl-2 sm:pl-3 pr-8 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-gray-900 dark:text-white"
                :class="
                  teamOpen
                    ? 'rounded-t-lg rounded-b-none border-b-gray-200 dark:border-b-gray-700'
                    : 'rounded-lg'
                "
              >
                <span class="block truncate">
                  {{ selectedTeamDisplayText }}
                </span>
                <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon
                    class="h-5 w-5 text-gray-400 dark:text-gray-500"
                    aria-hidden="true"
                  />
                </span>
              </ListboxButton>
              <transition
                leave-active-class="transition duration-100 ease-in"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <ListboxOptions
                  class="absolute z-50 w-max min-w-full max-w-[calc(100vw-2rem)] rounded-b-lg border border-t-0 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-1 text-sm shadow-lg focus:outline-none max-h-96 overflow-x-auto overflow-y-auto"
                >
                  <ListboxOption
                    v-for="team in teams"
                    :key="team.simplicate_id"
                    :value="team.simplicate_id"
                    v-slot="{ active, selected }"
                    as="template"
                  >
                    <li
                      :class="[
                        active
                          ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-900 dark:text-blue-200'
                          : 'text-gray-900 dark:text-gray-200',
                        'flex items-center gap-2 cursor-pointer select-none py-2 px-3'
                      ]"
                    >
                      <CheckIcon
                        v-if="selected"
                        class="h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400"
                        aria-hidden="true"
                      />
                      <span
                        :class="[
                          selected ? 'font-medium' : 'font-normal',
                          'block whitespace-nowrap'
                        ]"
                      >
                        {{ team.name
                        }}<template v-if="showEmployeeCounts">
                          ({{ getTeamEmployeeCount(team) ?? '…' }})</template
                        >
                      </span>
                    </li>
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </div>
          </Listbox>
        </div>

        <!-- Function multi-select Listbox -->
        <div class="relative">
          <label
            class="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2"
            >Functie</label
          >
          <Listbox
            v-slot="{ open: functionOpen }"
            :model-value="selectedFunctions"
            @update:model-value="updateFunctions"
            multiple
          >
            <div class="relative">
              <ListboxButton
                class="relative w-full cursor-pointer border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 pl-2 sm:pl-3 pr-8 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-gray-900 dark:text-white"
                :class="
                  functionOpen
                    ? 'rounded-t-lg rounded-b-none border-b-gray-200 dark:border-b-gray-700'
                    : 'rounded-lg'
                "
              >
                <span class="block truncate">
                  {{ selectedFunctionDisplayText }}
                </span>
                <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon
                    class="h-5 w-5 text-gray-400 dark:text-gray-500"
                    aria-hidden="true"
                  />
                </span>
              </ListboxButton>
              <transition
                leave-active-class="transition duration-100 ease-in"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <ListboxOptions
                  class="absolute z-50 w-max min-w-full max-w-[calc(100vw-2rem)] rounded-b-lg border border-t-0 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-1 text-sm shadow-lg focus:outline-none max-h-96 overflow-x-auto overflow-y-auto"
                >
                  <ListboxOption
                    v-for="func in functionsOptions"
                    :key="func"
                    :value="func"
                    v-slot="{ active, selected }"
                    as="template"
                  >
                    <li
                      :class="[
                        active
                          ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-900 dark:text-blue-200'
                          : 'text-gray-900 dark:text-gray-200',
                        'flex items-center gap-2 cursor-pointer select-none py-2 px-3'
                      ]"
                    >
                      <CheckIcon
                        v-if="selected"
                        class="h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400"
                        aria-hidden="true"
                      />
                      <span
                        :class="[
                          selected ? 'font-medium' : 'font-normal',
                          'block whitespace-nowrap'
                        ]"
                      >
                        {{ func
                        }}<template v-if="showEmployeeCounts">
                          ({{ getFunctionEmployeeCount(func) ?? '…' }})</template
                        >
                      </span>
                    </li>
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </div>
          </Listbox>
        </div>

        <!-- Aanspreekpunt multi-select Listbox -->
        <div v-if="aanspreekpunten && aanspreekpunten.length > 0" class="relative">
          <label
            class="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2"
            >Aanspreekpunt</label
          >
          <Listbox
            v-slot="{ open: aanspreekpuntOpen }"
            :model-value="selectedAanspreekpuntIds"
            @update:model-value="updateAanspreekpunten"
            multiple
          >
            <div class="relative">
              <ListboxButton
                class="relative w-full cursor-pointer border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 pl-2 sm:pl-3 pr-8 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-gray-900 dark:text-white"
                :class="
                  aanspreekpuntOpen
                    ? 'rounded-t-lg rounded-b-none border-b-gray-200 dark:border-b-gray-700'
                    : 'rounded-lg'
                "
              >
                <span class="block truncate">
                  {{ selectedAanspreekpuntDisplayText }}
                </span>
                <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon
                    class="h-5 w-5 text-gray-400 dark:text-gray-500"
                    aria-hidden="true"
                  />
                </span>
              </ListboxButton>
              <transition
                leave-active-class="transition duration-100 ease-in"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <ListboxOptions
                  class="absolute z-50 w-max min-w-full max-w-[calc(100vw-2rem)] rounded-b-lg border border-t-0 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-1 text-sm shadow-lg focus:outline-none max-h-96 overflow-x-auto overflow-y-auto"
                >
                  <ListboxOption
                    v-for="aanspreekpunt in aanspreekpunten"
                    :key="aanspreekpunt.id"
                    :value="aanspreekpunt.id"
                    v-slot="{ active, selected }"
                    as="template"
                  >
                    <li
                      :class="[
                        active
                          ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-900 dark:text-blue-200'
                          : 'text-gray-900 dark:text-gray-200',
                        'flex items-center gap-2 cursor-pointer select-none py-2 px-3'
                      ]"
                    >
                      <CheckIcon
                        v-if="selected"
                        class="h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400"
                        aria-hidden="true"
                      />
                      <span
                        :class="[
                          selected ? 'font-medium' : 'font-normal',
                          'block whitespace-nowrap'
                        ]"
                      >
                        {{ aanspreekpunt.name
                        }}<template v-if="showEmployeeCounts">
                          ({{ getAanspreekpuntEmployeeCount(aanspreekpunt) ?? '…' }})</template
                        >
                      </span>
                    </li>
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </div>
          </Listbox>
        </div>

        <!-- Availability mode single-select -->
        <AvailabilityModeSelect
          v-if="!hideAvailabilityMode"
          :model-value="selectedAvailabilityMode"
          @update:model-value="updateAvailabilityMode"
        />

        <!-- Activity category multi-select (list/timeline view) -->
        <ActivityCategorySelect
          v-if="showActivityCategories"
          :model-value="selectedActivityCategories"
          @update:model-value="updateActivityCategories"
        />

        <div>
          <label
            class="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2"
            >Van datum</label
          >
          <DatePickerLite
            :model-value="modelValue.start_date"
            @update:modelValue="val => update('start_date', val)"
            placeholder="Startdatum"
          />
        </div>
        <div>
          <label
            class="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2"
            >Tot datum</label
          >
          <DatePickerLite
            :model-value="modelValue.end_date"
            @update:modelValue="val => update('end_date', val)"
            placeholder="Einddatum"
          />
        </div>
      </div>
      <slot name="actions" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue'
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/vue/24/outline'
import DatePickerLite from './DatePickerLite.vue'
import AvailabilityModeSelect from './AvailabilityModeSelect.vue'
import ActivityCategorySelect from './ActivityCategorySelect.vue'
import { DEFAULT_ACTIVITY_CATEGORIES } from '@/constants/activityCategories.js'
import { employeeService, teamService } from '@/services/employeeService.js'

const props = defineProps({
  modelValue: { type: Object, required: true },
  functions: { type: Array, default: () => [] },
  aanspreekpunten: { type: Array, default: () => [] },
  showEmployeeCounts: { type: Boolean, default: false },
  hideAvailabilityMode: { type: Boolean, default: false },
  showActivityCategories: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue'])

const update = (key, value) => {
  emit('update:modelValue', { ...props.modelValue, [key]: value })
}

// Fetch teams
const { data: teams = [] } = useQuery({
  queryKey: ['teams'],
  queryFn: teamService.getAll,
  staleTime: 1000 * 60 * 10
})

const { data: employeesForCounts } = useQuery({
  queryKey: ['employees-filter-counts'],
  queryFn: () => employeeService.getAll({ is_sourceable: 'true', limit: 1000 }),
  enabled: computed(() => props.showEmployeeCounts),
  staleTime: 1000 * 60 * 10
})

// Computed unique functions from props
const functionsOptions = computed(() => Array.from(new Set(props.functions.filter(Boolean))).sort())

const employeesForCount = computed(() =>
  Array.isArray(employeesForCounts?.value) ? employeesForCounts.value : null
)

const employeeBelongsToTeam = (employee, teamId) =>
  (employee.teams || []).some(team => team.simplicate_id === teamId)

const matchesSelectedId = (selectedIds, value) =>
  selectedIds.some(selectedId => String(selectedId) === String(value))

const employeeMatchesSelectedAanspreekpunten = employee =>
  selectedAanspreekpuntIds.value.length === 0 ||
  matchesSelectedId(selectedAanspreekpuntIds.value, employee.aanspreekpunt_id)

const getTeamEmployeeCount = team => {
  const employees = employeesForCount.value
  if (!employees) return null

  return employees.filter(
    employee =>
      employeeBelongsToTeam(employee, team.simplicate_id) &&
      (selectedFunctions.value.length === 0 ||
        selectedFunctions.value.includes(employee.function)) &&
      employeeMatchesSelectedAanspreekpunten(employee)
  ).length
}

const getFunctionEmployeeCount = functionName => {
  const employees = employeesForCount.value
  if (!employees) return null

  return employees.filter(
    employee =>
      employee.function === functionName &&
      (selectedTeamIds.value.length === 0 ||
        selectedTeamIds.value.some(teamId => employeeBelongsToTeam(employee, teamId))) &&
      employeeMatchesSelectedAanspreekpunten(employee)
  ).length
}

const getAanspreekpuntEmployeeCount = aanspreekpunt => {
  const employees = employeesForCount.value
  if (!employees) return null

  return employees.filter(
    employee =>
      matchesSelectedId([aanspreekpunt.id], employee.aanspreekpunt_id) &&
      (selectedTeamIds.value.length === 0 ||
        selectedTeamIds.value.some(teamId => employeeBelongsToTeam(employee, teamId))) &&
      (selectedFunctions.value.length === 0 || selectedFunctions.value.includes(employee.function))
  ).length
}

// Get selected values as arrays (handle both old string format and new array format)
const selectedTeamIds = computed(() => {
  const val = props.modelValue.team_ids || props.modelValue.team_id
  if (!val) return []
  if (Array.isArray(val)) return val
  return val ? [val] : []
})

const selectedFunctions = computed(() => {
  const val = props.modelValue.functions || props.modelValue.function
  if (!val) return []
  if (Array.isArray(val)) return val
  return val ? [val] : []
})

const selectedAanspreekpuntIds = computed(() => {
  const val = props.modelValue.aanspreekpunt_ids || props.modelValue.aanspreekpunt_id
  if (!val) return []
  if (Array.isArray(val)) return val
  return val ? [val] : []
})

// Display text: show name when 1 selected, "X geselecteerd" when 2+
const selectedTeamDisplayText = computed(() => {
  if (selectedTeamIds.value.length === 0) return 'Alle teams'
  if (selectedTeamIds.value.length === 1) {
    const team = teams.value?.find(t => t.simplicate_id === selectedTeamIds.value[0])
    return team?.name || '1 geselecteerd'
  }
  return `${selectedTeamIds.value.length} geselecteerd`
})

const selectedFunctionDisplayText = computed(() => {
  if (selectedFunctions.value.length === 0) return 'Alle functies'
  if (selectedFunctions.value.length === 1) return selectedFunctions.value[0]
  return `${selectedFunctions.value.length} geselecteerd`
})

const selectedAanspreekpuntDisplayText = computed(() => {
  if (selectedAanspreekpuntIds.value.length === 0) return 'Alle aanspreekpunten'
  if (selectedAanspreekpuntIds.value.length === 1) {
    const aanspreekpunt = props.aanspreekpunten?.find(
      a => a.id === selectedAanspreekpuntIds.value[0]
    )
    return aanspreekpunt?.name || '1 geselecteerd'
  }
  return `${selectedAanspreekpuntIds.value.length} geselecteerd`
})

// Update functions for multi-select
const updateTeams = newValue => {
  update('team_ids', newValue)
}

const updateFunctions = newValue => {
  update('functions', newValue)
}

const updateAanspreekpunten = newValue => {
  update('aanspreekpunt_ids', newValue)
}

// Availability mode
const selectedAvailabilityMode = computed(() => {
  return props.modelValue.availability_mode || 'presourcing'
})

const updateAvailabilityMode = newValue => {
  update('availability_mode', newValue)
}

// Activity categories (list/timeline view)
const selectedActivityCategories = computed(() => {
  const val = props.modelValue.activity_categories
  return Array.isArray(val) ? val : DEFAULT_ACTIVITY_CATEGORIES
})

const updateActivityCategories = newValue => {
  update('activity_categories', newValue)
}
</script>
